import { useEffect, useState, useRef } from "react";
import { saveStep } from "../../../../services/stepService";
import { Check, ChevronRight, Loader2 } from "lucide-react";
import Button from "../../../ui/Button";
import TextField from "../../../ui/TextField";
import Sheet from "../../../ui/Sheet";
import WizardShell from "../../../ui/WizardShell";
import BottomBar from "../../../ui/BottomBar";
import BookSidePanel from "../../../ui/BookSidePanel";
import { useBuilderBack, useBuilderStepInterceptor } from "../../../builder/BuilderNavContext";
import { titleCanPrint, titleProblem } from "../../../../util/printableText";
import { BASE_BOOK_PRICE } from "../../../../config/pricing";
import { isEtsyUser } from "../../../../services/etsyService";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { updateBook } from "../../../../redux/slice/profileSlice";
import {
  BUILDER_ACTIONS,
  BUILDER_STEPS,
  countBuilderAction,
  setForewordBoxOpen,
  useBuilderStepVisit,
} from "../../../../util/builderStepTiming";
import { markStepAsVisited } from "../../../../redux/slice/profileSlice";
import { loadPendingCoverImage } from "../../../../util/pendingImageStorage";
import { FOREWORD_TITLE_MAX_LENGTH } from "../../../../util/forewordLimits";
import "../../../common/styles/Loader.css";
import { saveBookFields } from "../../../../services/trackedSaves";
import { CoverDrawFailedBox } from "../../../builder/CoverStatusStrip";
import { useCoverRetry } from "../../../builder/useCoverRetry";
import CoverPreview from "../../../builder/CoverPreview";
import { loadCoverFont, titleFitsCover } from "../../../../util/coverPage";

export const CoverEditor = ({ image }) => {
  const [bookTitle, setBookTitle] = useState(null);
  // Blank unless the customer typed one; "For you" is only the placeholder (a blank heading prints no heading).
  const [forewordTitle, setForewordTitle] = useState("");
  const [forewordContent, setForewordContent] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [titleOpen, setTitleOpen] = useState(false);
  // Phones: opening the title editor scrolls just enough for its Done to clear the bottom bar.
  const titleDoneRef = useRef(null);
  useEffect(() => {
    if (!titleOpen || !window.matchMedia?.("(max-width: 767.98px)").matches) return undefined;
    const frame = requestAnimationFrame(() => {
      const done = titleDoneRef.current;
      if (!done) return;
      const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--md-bottom-bar")) || 0;
      const overlap = done.getBoundingClientRect().bottom - (window.innerHeight - bar - 12);
      if (overlap > 0) window.scrollBy({ top: overlap, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [titleOpen]);
  const { data: profileData } = useSelector((state) => state.profile);
  const { images: pageImages } = useSelector((state) => state.sketch);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tag = searchParams.get("tag");
  // Arriving from Preview's empty dedication page (`note=1`): open the dedication editor, the pop-up on
  // phones and the fields on the page from tablet up, then drop the parameter (decided 2026-10-23).
  // `note=message` (Edit dedication in Preview's up-close view, decided 2026-10-26) does the same with
  // the cursor in the Message field rather than the Heading.
  const noteInlineRef = useRef(null);
  const noteSheetRef = useRef(null);
  const noteParam = searchParams.get("note");
  const openNoteOnArrival = noteParam === "1" || noteParam === "message";
  useEffect(() => {
    if (!openNoteOnArrival) return;
    // Drop the parameter first: any change to the address scrolls the page to the top (ScrollToTop),
    // so the scroll to the fields has to come after it.
    const fieldSelector = noteParam === "message" ? "textarea" : "input, textarea";
    const next = new URLSearchParams(searchParams);
    next.delete("note");
    setSearchParams(next, { replace: true });
    if (window.matchMedia?.("(max-width: 767.98px)").matches) {
      setNoteOpen(true);
      // The sheet's fields exist once it opens; the Message box gets the cursor (Heading is left as is).
      if (noteParam === "message") setTimeout(() => noteSheetRef.current?.querySelector("textarea")?.focus(), 250);
      return;
    }
    // Not cancelled on clean-up: dropping the parameter re-runs this effect straight away.
    setTimeout(() => {
      const field = noteInlineRef.current?.querySelector(fieldSelector);
      field?.scrollIntoView({ block: "center" });
      field?.focus({ preventScroll: true });
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNoteOnArrival]);
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);


  const { session } = useSelector((state) => state.authProvider);
  const { myBooks } = useSelector((state) => state.profile);
  const { coverImages: reduxCoverImages } = useSelector((state) => state.sketch);
  const { activeBookId, uploadingCoverImage } = useSelector((state) => state.stepFlow);
  useBuilderStepVisit(BUILDER_STEPS.DESIGN, activeBookId);

  // Check generation status once here for stability
  const pendingCover = activeBookId ? loadPendingCoverImage(activeBookId) : null;
  const isSentToRun = !!pendingCover?.sentToRun;
  // A drawn cover wins over the flags: the sent-to-run flag in local storage only
  // clears if this tab hears the "cover done" socket event, so a tab closed while
  // the cover was drawing would otherwise say "Drawing your cover" for good.
  // Same cover and "ready" test as the cover strip on Page photos: the first cover entry. Not
  // `find(isCover)`, which missed a cover reloaded from the server without the flag.
  const drawnCover = reduxCoverImages[0];
  const coverDrawn = !!(drawnCover?.AIUrl || drawnCover?.AIinfo?.aws_upload?.s3_url);
  const showCoverGenerating = !coverDrawn && (uploadingCoverImage || isSentToRun);

  // Set the book title dynamically
  useEffect(() => {
    const book = getBook(activeBookId);
    setBookTitle(book?.title ?? null);
    setForewordTitle(book?.introKeyword ?? "");
    setForewordContent(book?.description ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBooks.length, activeBookId]);

  const getBook = (bookId) => {
    return myBooks.find((book) => book._id === bookId) ?? null;
  };

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Get cover image URL from AIUrl
  const getCoverImageUrl = () => {
    return drawnCover?.AIUrl || drawnCover?.url || "";
  };

  const nextStep = async () => {
    try {
      // Get cover image URL
      const imageUrl = getCoverImageUrl();

      if (!!imageUrl) {
        saveBookFields({
          id: activeBookId,
          title: bookTitle,
          coverImage: imageUrl,
          introKeyword: forewordTitle,
          description: forewordContent,
        });
      }

      dispatch(updateBook({ _id: activeBookId, coverImage: imageUrl }));
      // Navigate immediately
      dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Review_Your_Book' }))
      navigate(`${"/review"}?stepCode=${"Review_Your_Book"}${tag ? `&tag=${tag}` : ""}`);

      // Continue with step saving in the background
      saveStep({
        userId: session?.user?._id,
        bookId: activeBookId,
        isCompleted: 'yes',
        stepCode: "Review_Your_Book",
      }).catch(err => console.error("Error saving step in background:", err));
    } catch (err) {
      console.warn("Error in nextStep:", err);
    }
  };

  const hasCoverImage = getCoverImageUrl() !== "";

  // ---- step 4 on the redesign (docs/design/HANDOFF.md: Design, Dedication, Tab4Design, Desk4Design)

  const book = getBook(activeBookId);
  const showPrice = !isEtsyUser(profileData) && !book?.gift?.giftOrderId;
  const pageCount = (pageImages || []).filter((img) => !img.isCover && !img.deletedAt).length || book?.pageCount || 20;
  const MESSAGE_MAX = 700;

  // Blur saves; a Save button or a second blur with nothing changed must not PUT (or toast) again.
  const savedTitleRef = useRef(null);
  const savedNoteRef = useRef(null);
  const saveTitle = async () => {
    const next = (bookTitle || "").trim();
    // A title with nothing printable is never saved, so the book keeps its last good one.
    if (!titleCanPrint(next) || savedTitleRef.current === next) return;
    savedTitleRef.current = next;
    dispatch(updateBook({ _id: activeBookId, title: next }));
    await saveBookFields({ id: activeBookId, title: next });
  };
  const saveNote = async () => {
    const heading = (forewordTitle || "").trim();
    const key = `${heading}\u0000${forewordContent || ""}`;
    if (savedNoteRef.current === key) return;
    savedNoteRef.current = key;
    dispatch(updateBook({ _id: activeBookId, introKeyword: heading, description: forewordContent || "" }));
    await saveBookFields({ id: activeBookId, introKeyword: heading, description: forewordContent || "" });
  };
  const goBackToPhotos = () => {
    dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: "Upload_Images" }));
    navigate(`/create?stepCode=Upload_Images${tag ? `&tag=${tag}` : ""}`);
    saveStep({ userId: session?.user?._id, bookId: activeBookId, isCompleted: "yes", stepCode: "Upload_Images" }).catch((err) =>
      console.error("Error saving step in background:", err)
    );
  };
  useBuilderBack(goBackToPhotos);

  // Nobody leaves for Preview without a title that prints. Waits for the book to load before judging it.
  const titleBlocked = !!book && !titleCanPrint(bookTitle ?? book.title);
  useBuilderStepInterceptor(({ stepNumber, navigate: go }) => {
    if (stepNumber > 4 && titleBlocked) {
      setTitleOpen(true);
      return;
    }
    go();
  });
  useEffect(() => {
    if (titleBlocked) setTitleOpen(true);
  }, [titleBlocked]);

  // The cover failed to draw (decided 2026-10-23: same message and buttons as Page photos).
  const { failed: coverFailed, retry: retryCover, retrying: coverRetrying, usedUp: coverUsedUp } = useCoverRetry(activeBookId, drawnCover);
  const goToCoverPhoto = () => {
    dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: "Upload_Your_Cover_Photo" }));
    navigate(`/dashboard?stepCode=Upload_Your_Cover_Photo${tag ? `&tag=${tag}` : ""}`);
    saveStep({ userId: session?.user?._id, bookId: activeBookId, isCompleted: "yes", stepCode: "Upload_Your_Cover_Photo" }).catch((err) =>
      console.error("Error saving step in background:", err)
    );
  };

  const coverCard = (
    // The cover as it prints, updating as the title is typed. Smaller on phones (212 wide, decided 2026-10-23)
    // so the title and dedication rows under it are on screen; 264 from tablet up.
    <div className="w-[212px] flex-shrink-0 overflow-hidden rounded-xl shadow-[0_8px_24px_rgba(60,40,30,0.18)] md:w-[264px]">
      <CoverPreview
        title={bookTitle}
        alt={bookTitle || "Your cover"}
        imageUrl={!coverFailed && !showCoverGenerating && hasCoverImage ? getCoverImageUrl() : null}
        placeholder={
          coverFailed ? (
            <CoverDrawFailedBox onRetry={retryCover} onChangePhoto={goToCoverPhoto} retrying={coverRetrying} usedUp={coverUsedUp} hideIconOnPhones />
          ) : showCoverGenerating ? (
            <div role="status" className="flex h-full flex-col items-center justify-center gap-2 bg-[#F6F2EC] text-md-pink-dark">
              <Loader2 className="h-7 w-7 animate-spin" aria-hidden="true" />
              <span className="text-sm font-semibold">Drawing your cover</span>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-sm font-semibold text-[#6B4E44]">No cover yet</div>
          )
        }
      />
    </div>
  );

  // TODO(design): the "fits on the cover" message isn't drawn (docs/design/GAPS.md).
  const [titleFull, setTitleFull] = useState(false);
  useEffect(loadCoverFont, []);
  const titleField = (
    <TextField
      label="Cover title"
      value={bookTitle || ""}
      // No "N/40" counter: what fits depends on the letters, not the count (decided 2026-10-23).
      maxLength={40}
      helper={titleFull ? "That's as long as fits on the cover." : undefined}
      // TODO(design): this message isn't drawn (docs/design/GAPS.md).
      error={titleBlocked ? titleProblem(bookTitle) : undefined}
      onChange={(e) => {
        // Saved as typed (decided 2026-10-23); the cover preview and the print set it in capitals themselves.
        const next = e.target.value;
        if (next.length > 40) return;
        // Like the live app's title box: no third line on the cover (it would print smaller, on 3 lines).
        if (next.length > (bookTitle || "").length && !titleFitsCover(next)) {
          setTitleFull(true);
          return;
        }
        setTitleFull(false);
        setBookTitle(next);
        if (titleCanPrint(next)) dispatch(updateBook({ _id: activeBookId, title: next }));
      }}
      onBlur={saveTitle}
    />
  );

  const noteFields = (
    <div className="flex flex-col gap-4 text-left">
      <TextField
        label="Heading"
        placeholder="For you"
        value={forewordTitle || ""}
        maxLength={FOREWORD_TITLE_MAX_LENGTH}
        onChange={(e) => {
          if (e.target.value.length > FOREWORD_TITLE_MAX_LENGTH) return;
          setForewordTitle(e.target.value);
        }}
        onBlur={saveNote}
      />
      {/* Times the Message box for the Analytics page's Forewords card (as the old step 4 did; App 91a8643):
          how long it's selected, how often, and pastes. Never the text. Same box on the phone sheet and inline. */}
      <div
        onFocus={() => setForewordBoxOpen(true)}
        onBlur={() => setForewordBoxOpen(false)}
        onPaste={() => countBuilderAction(BUILDER_ACTIONS.FOREWORD_PASTED)}
      >
        <TextField
          label="Message"
          multiline
          rows={5}
          placeholder="Write a note to the person receiving this book"
          value={forewordContent || ""}
          maxLength={MESSAGE_MAX}
          showCount
          onChange={(e) => {
            if (e.target.value.length > MESSAGE_MAX) return;
            setForewordContent(e.target.value);
          }}
          onBlur={saveNote}
        />
      </div>
    </div>
  );

  const primary = (
    <Button size="lg" fullWidth onClick={nextStep} disabled={titleBlocked}>
      Next: Preview book
    </Button>
  );
  const sidePanel = (
    <BookSidePanel
      cover={<CoverPreview title={bookTitle} imageUrl={hasCoverImage && !showCoverGenerating && !coverFailed ? getCoverImageUrl() : null} />}
      title={bookTitle}
      rows={[{ label: `Cover + ${pageCount} pages`, value: "" }, ...(showPrice ? [{ label: "Price", value: `$${BASE_BOOK_PRICE.toFixed(2)}` }] : [])]}
      primary={primary}
      back={{ onClick: goBackToPhotos }}
      compact
    >
      <div className="flex flex-col gap-1.5 text-sm text-md-dark">
        {["Title", "Cover photo", `${pageCount} page photos`].map((item) => (
          <span key={item} className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-800" strokeWidth={3} aria-hidden="true" />
            {item}
          </span>
        ))}
      </div>
    </BookSidePanel>
  );

  return (
    <>
      <WizardShell aside={sidePanel} bottomBar={<BottomBar primary={primary} back={{ onClick: goBackToPhotos }} />}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">
              {/* TODO(design): the heading for a failed cover isn't drawn (docs/design/GAPS.md). */}
              {coverFailed ? "Your cover" : showCoverGenerating ? "Drawing your cover" : "Your cover is ready"}
            </h1>
            {/* Not on phones (decided 2026-10-23): the room goes to a bigger cover. */}
            <p className="m-0 hidden text-[15px] leading-snug text-md-muted md:block md:text-[17px]">
              Check the title, then add a note for the first page if you like.
            </p>
          </div>

          <div className="flex flex-col items-center gap-6 md:flex-row md:items-start md:gap-8">
            {coverCard}

            {/* Mobile and tablet: two rows that open editors. Desktop: the fields inline. */}
            <div className="flex w-full flex-col overflow-hidden rounded-[14px] border border-md-pink-light-1 bg-white md:hidden">
              {titleOpen ? (
                <div className="flex flex-col gap-3 border-b border-md-pink-light-1 p-4">
                  {titleField}
                  <Button ref={titleDoneRef} variant="text" className="self-end" onClick={() => !titleBlocked && setTitleOpen(false)}>
                    Done
                  </Button>
                </div>
              ) : (
                <button type="button" onClick={() => setTitleOpen(true)} className="flex min-h-[64px] items-center gap-3 border-b border-md-pink-light-1 px-4 py-2.5 text-left">
                  <span className="flex flex-1 flex-col gap-0.5">
                    <span className="text-[13px] text-md-muted">Cover title</span>
                    <span className="text-base font-semibold text-md-dark">{bookTitle}</span>
                  </span>
                  <span className="text-[15px] font-semibold text-md-pink-dark">Edit</span>
                </button>
              )}
              <button type="button" onClick={() => setNoteOpen(true)} className="flex min-h-[64px] items-center gap-3 px-4 py-2.5 text-left">
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-[13px] text-md-muted">Dedication page · optional</span>
                  {/* One line, cut off with "…", so the row keeps its height and fits under the cover on phones. */}
                  <span className="truncate text-base font-semibold text-md-dark">{forewordContent?.trim() ? `${forewordTitle?.trim() ? `${forewordTitle.trim()} · ` : ""}${forewordContent.trim().slice(0, 40)}${forewordContent.trim().length > 40 ? "…" : ""}` : "Add a personal note"}</span>
                </span>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-md-pink/10 text-md-pink-dark">
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </span>
              </button>
            </div>

            <div className="hidden w-full min-w-0 flex-col gap-5 md:flex">
              {titleField}
              <div className="flex flex-col gap-1 border-t border-md-pink-light-1 pt-5">
                <div className="text-[15px] font-bold text-md-dark">
                  Dedication page <span className="font-normal text-md-muted">· optional</span>
                </div>
                <div className="text-sm text-md-muted">Prints on the first page, right after the cover.</div>
              </div>
              <div ref={noteInlineRef}>{noteFields}</div>
            </div>
          </div>
        </div>
      </WizardShell>

      <Sheet
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        title="Dedication page"
        actions={
          <>
            <Button
              size="lg"
              fullWidth
              onClick={() => setNoteOpen(false)}
            >
              Save note
            </Button>
            <Button variant="text" fullWidth className="text-md-dark" onClick={() => setNoteOpen(false)}>
              Skip for now
            </Button>
          </>
        }
      >
        <p className="m-0 w-full text-left text-[15px] leading-snug text-md-muted">Prints on the first page, right after the cover. Leave it blank to skip.</p>
        <div ref={noteSheetRef} className="w-full">{noteFields}</div>
      </Sheet>
    </>
  );
};
